<script setup lang="ts">
  import { ref } from 'vue';
  import { SearchCombobox, type SearchComboboxOption } from '@/shared/ui/search-combobox';
  import { MAX_PROCEDURES } from '../../lib/record-config-options';
  import RecordConfigRepeater from './RecordConfigRepeater.vue';
  import type { RecordConfigProcedureOption } from '../../api/record-config-options.api';

  const PLACEHOLDER = 'Selecione ou busque o procedimento';
  const EMPTY_MESSAGE = 'Nenhum procedimento encontrado.';

  const props = defineProps<{
    modelValue: Array<number | null>;
    procedures: RecordConfigProcedureOption[];
    isLoading: boolean;
  }>();

  const emit = defineEmits<{
    'update:modelValue': [value: Array<number | null>];
  }>();

  const searchTerms = ref<Record<number, string>>({});

  function setSearchTerm(index: number, term: string): void {
    searchTerms.value = { ...searchTerms.value, [index]: term };
  }

  function toComboboxValue(value: number | null): string {
    return value === null ? '' : String(value);
  }

  function fromComboboxValue(value: string): number | null {
    return value === '' ? null : Number(value);
  }

  function optionsFor(index: number, ownValue: number | null): SearchComboboxOption[] {
    const takenElsewhere = new Set(
      props.modelValue.filter((value, rowIndex) => rowIndex !== index && value !== null)
    );
    const term = (searchTerms.value[index] ?? '').trim().toLowerCase();

    return props.procedures
      .filter(
        ({ documentRevisionId }) =>
          documentRevisionId === ownValue || !takenElsewhere.has(documentRevisionId)
      )
      .filter(({ code }) => term === '' || code.toLowerCase().includes(term))
      .map(({ documentRevisionId, code }) => ({ value: String(documentRevisionId), label: code }));
  }

  function updateRows(rows: Array<number | null>): void {
    searchTerms.value = {};
    emit('update:modelValue', rows);
  }
</script>

<template>
  <div class="flex flex-col gap-4 px-8 py-6" data-testid="record-config-procedures">
    <span class="text-sm font-semibold text-gray-07"
      >Procedimento(s) vinculado(s) ao Registro:</span
    >

    <RecordConfigRepeater
      :model-value="modelValue"
      :empty-value="null"
      :max="MAX_PROCEDURES"
      add-label="Adicionar procedimento"
      remove-label="Remover procedimento"
      @update:model-value="updateRows"
    >
      <template #default="{ value, index, update }">
        <SearchCombobox
          :model-value="toComboboxValue(value)"
          :options="optionsFor(index, value)"
          :loading="isLoading"
          :placeholder="PLACEHOLDER"
          :empty-message="EMPTY_MESSAGE"
          trigger-icon="search"
          class="w-[28rem] max-w-full bg-white-01"
          @search="setSearchTerm(index, $event)"
          @update:model-value="update(fromComboboxValue($event))"
        />
      </template>
    </RecordConfigRepeater>
  </div>
</template>
